import {aiAgentRoutes} from '@ai/ai-agent/ai-agent-routes';
import {adminQueries} from '@app/admin/admin-queries';
import {cannedRepliesRoutes} from '@app/canned-replies/canned-replies-routes';
import {customersRoutes} from '@app/dashboard/contacts/customers-routes';
import {helpdeskReportRoutes} from '@app/dashboard/reports/helpdesk-reports-routes';
import {statusesRoutes} from '@app/dashboard/statuses/statuses-routes';
import {teamRoutes} from '@app/dashboard/team-routes';
import {viewsRoutes} from '@app/dashboard/views/views-routes';
import {hcManagerRoutes} from '@app/help-center/manager/hc-manager-routes';
import {adminCustomPagesRoutes} from '@common/admin/custom-pages/admin-custom-pages-routes';
import {adminFileEntriesRoutes} from '@common/admin/file-entry/admin-file-entries-routes';
import {adminLogsRoutes} from '@common/admin/logging/admin-logs-routes';
import {adminRolesRoutes} from '@common/admin/roles/admin-roles-routes';
import {commonAdminSettingsRoutes} from '@common/admin/settings/common-admin-settings-routes';
import {adminTagsRoutes} from '@common/admin/tags/admin-tags-routes';
import {adminLocalizationsRoutes} from '@common/admin/translations/admin-localizations-routes';
import {authGuard} from '@common/auth/guards/auth-route';
import {shouldRevalidateDatatableLoader} from '@common/datatable/filters/utils/should-revalidate-datatable-loader';
import {queryClient} from '@common/http/query-client';
import {campaignsRoutes} from '@livechat/dashboard/campaigns/campaigns-routes';
import {searchParamsFromUrl} from '@ui/utils/urls/search-params-from-url';
import {redirect, RouteObject} from 'react-router';
import {Fragment} from 'react/jsx-runtime';
import {StaffPageGate, staffPageGuard} from '@common/auth/guards/staff-page-gate';
import {canOpenStaffPage} from '@common/auth/staff-page-access';
import {auth} from '@common/auth/use-auth';
import {directoryAdminRoutes} from '@app/opportunities/admin/directory-dashboard-routes';

export const adminRoutes: RouteObject[] = [
  {
    path: 'admin',
    // Anyone with at least one admin page; each page checks its own permission
    // (config/staff-access.php).
    loader: ({request}) => staffPageGuard(request.url),
    lazy: () => import('@common/admin/admin-layout'),
    children: [
     {
      // Every admin page checks its own permission (config/staff-access.php).
      element: <StaffPageGate />,
      children: [
      {
        // The overview, or the first admin page this person can open.
        index: true,
        loader: () => {
          const has = (name: string) => auth.hasPermission(name);
          const first = [
            '/admin/dashboard', '/admin/directory/listings', '/admin/customers', '/admin/roles',
            '/admin/services', '/admin/volunteers', '/admin/referrals', '/admin/appearance',
            '/admin/settings/general', '/admin/logs',
          ].find(path => canOpenStaffPage(path, has));
          return redirect(first ?? '/dashboard');
        },
        element: <Fragment />,
      },
      {
        path: 'dashboard',
        lazy: () => import('@app/admin/dashboard/admin-dashboard-page'),
      },
      // Student lookup for support. Inherits the `admin.access` guard on the
      // parent route, so it uses the same permission system as every other
      // admin page rather than introducing a second one.
      {
        path: 'students',
        lazy: () => import('@app/admin/students/admin-students-page'),
      },
      // Direct slugs for the sections inside that page, so each can be opened
      // and shared on its own URL rather than only reached by clicking a tab.
      {
        path: 'students/:section',
        lazy: () => import('@app/admin/students/admin-students-page'),
      },
      {
        path: 'register-form',
        lazy: () =>
          import('@app/admin/register-form/register-form-rules-page'),
      },
      ...helpdeskReportRoutes,
      ...teamRoutes,
      ...customersRoutes,
      ...campaignsRoutes,
      ...viewsRoutes,
      ...statusesRoutes,
      ...hcManagerRoutes,
      ...cannedRepliesRoutes(false),
      ...aiAgentRoutes,

      // Team members (staff → roles → access) — before roles/:roleId so the
      // literal path wins.
      {
        path: 'roles/members',
        lazy: () => import('@app/admin/staff-access/team-members-page'),
      },
      {
        path: 'staff-agreement',
        lazy: () => import('@app/admin/staff-access/staff-agreement-page'),
      },
      ...Object.values(adminRolesRoutes).map(
        (route): RouteObject =>
          route.path === 'roles/:roleId/edit'
            ? ({
                ...route,
                children: route.children?.map(
                  (child): RouteObject =>
                    child.path === 'users'
                      ? {path: 'users', lazy: () => import('@app/admin/staff-access/role-members-panel')}
                      : child,
                ),
              } as RouteObject)
            : route,
      ),
      ...Object.values(adminCustomPagesRoutes),
      ...Object.values(adminTagsRoutes),
      ...Object.values(adminLocalizationsRoutes),
      ...Object.values(adminFileEntriesRoutes),
      ...Object.values(adminLogsRoutes),

      commonAdminSettingsRoutes(
        [
          {
            path: 'tickets',
            lazy: () => import('@app/admin/settings/tickets-settings'),
          },
          {
            path: 'search',
            lazy: () =>
              import(
                '@common/admin/settings/pages/search-settings/search-settings'
              ),
          },
          {
            path: 'envato',
            lazy: () =>
              import('@app/admin/settings/envato-settings/envato-settings'),
          },
          {
            path: 'hc',
            lazy: () => import('@app/admin/settings/hc/hc-settings'),
          },
          {
            path: 'email/incoming',
            lazy: () =>
              import(
                '@common/admin/settings/pages/email-settings/incoming-email/incoming-email-settings'
              ),
          },
          {
            path: 'livechat',
            lazy: () => import('@livechat/admin/settings/livechat-settings'),
          },
          {
            path: 'ai',
            lazy: () => import('@ai/admin/settings/ai-settings-page'),
          },
          {
            path: 'crm',
            lazy: () => import('@app/crm/crm-settings-page'),
          },
          {
            // Partner programme settings, in the settings area where an admin
            // looks for them. The same controls also sit on the programme
            // screen's own Settings tab.
            path: 'referrals',
            lazy: () =>
              import('@app/admin/settings/programmes/referral-settings-page'),
          },
          {
            path: 'volunteers',
            lazy: () =>
              import('@app/admin/settings/programmes/volunteer-settings-page'),
          },
          {
            path: 'opportunities',
            lazy: () => import('@app/admin/settings/opportunities-settings-page'),
          },
          {
            path: 'landing-page',
            lazy: () =>
              import(
                '@common/admin/settings/landing-page-settings/landing-page-settings'
              ),
          },
        ],
        {
          captcha: {
            lazy: () => import('@app/admin/settings/captcha-settings'),
          },
          system: {
            lazy: () =>
              import(
                '@common/admin/settings/pages/system-settings/system-settings'
              ),
          },
        },
      ),

      // Volunteers
      {
        path: 'volunteers',
        lazy: () => import('@app/volunteers/admin/volunteers-datatable-page'),
      },
      {
        path: 'volunteers/:volunteerId',
        lazy: () => import('@app/volunteers/admin/volunteer-detail-page'),
      },
      // Volunteer Activities
      {
        path: 'volunteer-activities',
        lazy: () => import('@app/volunteers/admin/activities-datatable-page'),
      },
      {
        path: 'volunteer-activities/new',
        lazy: () => import('@app/volunteers/admin/activity-builder-page'),
      },
      {
        path: 'volunteer-activities/:activityId/edit',
        lazy: () => import('@app/volunteers/admin/activity-builder-page'),
      },
      {
        path: 'volunteer-activities/:activityId/attendance',
        lazy: () => import('@app/volunteers/admin/activity-attendance-page'),
      },
      // Certificates
      {
        path: 'certificates',
        lazy: () => import('@app/volunteers/admin/certificates-datatable-page'),
      },
      {
        path: 'certificate-templates',
        lazy: () => import('@app/volunteers/admin/certificate-templates-page'),
      },

      // CRM Sync Logs (standalone page)
      {
        path: 'crm-sync-logs',
        lazy: () => import('@app/crm/crm-sync-logs-page'),
      },
      // Follow-up tasks Flash raises in the CRM: templates, roster, recent.
      {
        path: 'crm-follow-up-tasks',
        lazy: () => import('@app/crm/crm-follow-up-tasks-page'),
      },
      // One-time sign-in codes: WhatsApp (via the CRM's WhatsBoost number) + email.
      {
        path: 'login-codes',
        lazy: () => import('@app/crm/login-codes-settings-page'),
      },

      // Directory / Opportunities — the same screens also live on the staff
      // dashboard at /dashboard/directory (directory-dashboard-routes.tsx).
      ...directoryAdminRoutes,
      {
        path: 'directory/listings/:listingId/edit',
        lazy: () => import('@app/directory/admin/directory-listing-form-page'),
      },
      {
        path: 'directory/countries',
        lazy: () => import('@app/directory/admin/directory-countries-datatable-page'),
      },
      {
        path: 'directory/reviews',
        lazy: () => import('@app/directory/admin/directory-reviews-datatable-page'),
      },
      {
        path: 'directory/saved',
        lazy: () => import('@app/directory/admin/directory-saved-datatable-page'),
      },

      // Onboarding
      {
        path: 'onboarding',
        lazy: () => import('@app/onboarding/admin/onboarding-flows-datatable-page'),
      },
      {
        path: 'onboarding/new',
        lazy: () => import('@app/onboarding/admin/onboarding-flow-form-page'),
      },
      {
        path: 'onboarding/:flowId/edit',
        lazy: () => import('@app/onboarding/admin/onboarding-flow-form-page'),
      },

      // Advertisements
      {
        path: 'advertisements',
        lazy: () => import('@app/advertisements/admin/advertisements-datatable-page'),
      },
      {
        path: 'advertisements/new',
        lazy: () => import('@app/advertisements/admin/advertisement-form-page'),
      },
      {
        path: 'advertisements/:adId/edit',
        lazy: () => import('@app/advertisements/admin/advertisement-form-page'),
      },

      // Platform Integration
      {
        path: 'integration',
        lazy: () => import('@app/integration/integration-settings-page'),
      },
      {
        path: 'landing-builder',
        lazy: () =>
          import('@app/landing-builder/admin/landing-builder-page'),
      },

      // Form Wizards
      {
        path: 'wizards',
        lazy: () => import('@app/wizards/admin/wizards-datatable-page'),
      },
      {
        path: 'wizards/new',
        lazy: () => import('@app/wizards/admin/wizard-builder-page'),
      },
      {
        path: 'wizards/:wizardId/edit',
        lazy: () => import('@app/wizards/admin/wizard-builder-page'),
      },
      {
        path: 'wizards/:wizardId/submissions',
        lazy: () => import('@app/wizards/admin/wizard-submissions-page'),
      },

      // services
      {
        path: 'services',
        lazy: () =>
          import('@app/services/admin/services-datatable-page'),
      },
      {
        path: 'services/new',
        lazy: () => import('@app/services/admin/service-form-page'),
      },
      {
        path: 'services/:serviceId/edit',
        lazy: () => import('@app/services/admin/service-form-page'),
      },
      {
        // Choose-library: reusable categories / deliverables / requirements /
        // durations / add-ons that services are assembled from.
        path: 'service-options',
        lazy: () => import('@app/services/admin/service-options-page'),
      },
      {
        path: 'service-requests',
        lazy: () =>
          import('@app/services/admin/service-requests-datatable-page'),
      },
      {
        path: 'service-packages',
        lazy: () =>
          import('@app/services/admin/service-packages-datatable-page'),
      },
      {
        path: 'coupons',
        lazy: () => import('@app/checkout/admin/coupons-admin-page'),
      },
      {
        path: 'abandoned-carts',
        lazy: () => import('@app/checkout/admin/abandoned-carts-page'),
      },
      {
        path: 'email-templates',
        lazy: () => import('@app/admin/email-templates/email-templates-page'),
      },
      {
        // Did last night's backup of the site and the CRM actually happen?
        path: 'backups',
        lazy: () => import('@app/admin/backups/backups-page'),
      },
      {
        // Volunteer suggestion review queue.
        path: 'content-suggestions',
        lazy: () => import('@app/admin/content-suggestions-page'),
      },
      {
        // Shared Appearance layout (breadcrumb) + central hub index. All
        // appearance pages nest here so the breadcrumb lives in one place
        // (default layout) instead of being added to each page.
        path: 'appearance',
        lazy: () => import('@app/appearance/admin/appearance-admin-layout'),
        children: [
          {
            index: true,
            lazy: () => import('@app/appearance/admin/appearance-hub-page'),
          },
          {
            path: 'theme-options',
            lazy: () => import('@app/appearance/theme-options/theme-options-page'),
          },
          {
            path: 'builder',
            lazy: () => import('@app/appearance/builder/blocks-builder-page'),
          },
          {
            path: 'live',
            lazy: () => import('@app/appearance/live-editor/live-editor-page'),
          },
          {
            path: 'themes',
            lazy: () => import('@app/appearance/themes/themes-page'),
          },
          {
            path: 'content',
            lazy: () => import('@app/appearance/content/content-page'),
          },
        ],
      },
      {
        path: 'service-packages/new',
        lazy: () => import('@app/services/admin/service-package-form-page'),
      },
      {
        path: 'service-packages/:packageId/edit',
        lazy: () => import('@app/services/admin/service-package-form-page'),
      },
      {
        path: 'placement-exams',
        lazy: () =>
          import('@app/services/admin/placement-exams-datatable-page'),
      },
      {
        path: 'placement-exams/new',
        lazy: () =>
          import('@app/services/admin/placement-exam-form-page'),
      },
      {
        path: 'placement-exams/:id/edit',
        lazy: () =>
          import('@app/services/admin/placement-exam-form-page'),
      },
      {
        path: 'placement-exams/:id/attempts',
        lazy: () =>
          import('@app/services/admin/placement-exam-attempts-page'),
      },
      {
        // The partner programme in full: partners, the commission ledger,
        // payouts, and every rule the programme runs on. Supersedes the
        // partners page below, which only ever listed applications.
        path: 'referrals',
        lazy: () => import('@app/referrals/admin/referral-admin-page'),
      },
      {
        // The agreement partner applicants read and accept.
        path: 'referrals/agreement',
        lazy: () => import('@app/referrals/admin/partner-agreement-page'),
      },
      {
        // The volunteer programme: the review queue, applications,
        // certificates due, the permission model and the settings.
        path: 'volunteer-programme',
        lazy: () => import('@app/volunteers/admin/volunteer-admin-page'),
      },
      {
        // Referral partners: applications, approvals, earnings. The endpoints
        // existed with no screen behind them.
        path: 'partners',
        lazy: () => import('@app/services/admin/partners-page'),
      },
      {
        // Whether the partner / company / volunteer programmes are open, and
        // on what terms.
        path: 'programmes',
        lazy: () => import('@app/services/admin/programmes-page'),
      },
      {
        // Cross-test analysis: starts vs completions, signed-out share, and
        // what the results cluster around. Signed-out attempts are counted
        // here and nowhere else.
        path: 'assessments-overview',
        lazy: () =>
          import('@app/services/admin/assessments-overview-page'),
      },
      {
        path: 'membership-plans',
        lazy: () =>
          import('@app/services/admin/membership-plans-page'),
      },

      // attributes
      {
        path: 'attributes',
        shouldRevalidate: shouldRevalidateDatatableLoader,
        lazy: () =>
          import('@app/attributes/datatable/attributes-datatable-page'),
      },
      {
        path: 'attributes/new',
        lazy: () => import('@app/attributes/crupdate/create-attribute-page'),
      },
      {
        path: 'attributes/:id/edit',
        lazy: () => import('@app/attributes/crupdate/update-attribute-page'),
      },

      // triggers
      {
        path: 'triggers',
        shouldRevalidate: shouldRevalidateDatatableLoader,
        lazy: () => import('@app/triggers/triggers-datatable-page'),
        loader: async ({request}) => {
          const redirect = authGuard({permission: 'triggers.update'});
          if (redirect) return redirect;
          return await queryClient.ensureQueryData(
            adminQueries.triggers.index(searchParamsFromUrl(request.url)),
          );
        },
      },
      {
        path: 'triggers/new',
        lazy: () => import('@app/triggers/create-trigger-page'),
        loader: async () => {
          const redirect = authGuard({permission: 'triggers.update'});
          if (redirect) return redirect;
          return await queryClient.ensureQueryData(
            adminQueries.triggers.config(),
          );
        },
      },
      {
        path: 'triggers/:triggerId/edit',
        lazy: () => import('@app/triggers/update-trigger-page'),
        loader: async ({params}) => {
          const redirect = authGuard({permission: 'triggers.update'});
          if (redirect) return redirect;
          return await Promise.allSettled([
            queryClient.ensureQueryData(
              adminQueries.triggers.get(params.triggerId!),
            ),
            queryClient.ensureQueryData(adminQueries.triggers.config()),
          ]);
        },
      },
    ],
     },
    ],
  },
];
